import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  List,
  ListItem,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Send as SendIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  EmojiEmotions as EmojiIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { chatService } from '../../services/chat';
import EmojiPicker from 'emoji-picker-react';

function Chat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [ws, setWs] = useState(null);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  // Auto scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load chat history
  useEffect(() => {
    const loadHistory = async () => {
      const result = await chatService.getChatHistory();
      if (result.success) {
        setMessages(result.data);
      }
      setLoading(false);
    };
    loadHistory();
  }, []);

  // WebSocket connection
  useEffect(() => {
    const connectWebSocket = () => {
      const wsUrl = chatService.getWebSocketUrl();
      const websocket = new WebSocket(wsUrl);

      websocket.onopen = () => {
        console.log('WebSocket connected');
        setConnected(true);
      };

      websocket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'message') {
          // Check if message already exists to prevent duplicates
          setMessages(prev => {
            const exists = prev.some(msg => msg._id === data._id);
            if (exists) {
              return prev; // Don't add duplicate
            }
            return [...prev, data];
          });
        } else if (data.type === 'user_joined' || data.type === 'user_left') {
          setOnlineUsers(data.online_users || []);
        }
      };

      websocket.onclose = () => {
        console.log('WebSocket disconnected');
        setConnected(false);
        // Attempt reconnection after 3 seconds
        setTimeout(connectWebSocket, 3000);
      };

      websocket.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      wsRef.current = websocket;
      setWs(websocket);
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const handleSendMessage = () => {
    if (newMessage.trim() && ws && connected) {
      ws.send(JSON.stringify({
        type: 'message',
        message: newMessage.trim()
      }));
      setNewMessage('');
      setShowEmojiPicker(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const onEmojiClick = (emojiObject) => {
    setNewMessage(prev => prev + emojiObject.emoji);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ height: 600, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChatIcon />
          <Typography variant="h6">Global Chat</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip 
            label={connected ? 'Connected' : 'Disconnected'} 
            color={connected ? 'success' : 'error'}
            size="small"
          />
          <Chip 
            icon={<PersonIcon />}
            label={`${onlineUsers.length} online`}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
          />
        </Box>
      </Box>

      {!connected && (
        <Alert severity="warning" sx={{ m: 2 }}>
          Connecting to chat server...
        </Alert>
      )}

      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        <List>
          {messages.map((msg, index) => {
            const isOwnMessage = msg.user_id === (user?.id || user?._id);
            return (
              <ListItem 
                key={msg._id || index}
                sx={{
                  flexDirection: 'column',
                  alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
                  py: 0.5
                }}
              >
                <Box sx={{
                  display: 'flex',
                  flexDirection: isOwnMessage ? 'row-reverse' : 'row',
                  alignItems: 'flex-start',
                  gap: 1,
                  maxWidth: '70%'
                }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: isOwnMessage ? 'primary.main' : 'secondary.main' }}>
                    {msg.username?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {msg.username} • {new Date(msg.timestamp).toLocaleTimeString()}
                    </Typography>
                    <Paper
                      sx={{
                        p: 1.5,
                        bgcolor: isOwnMessage ? 'primary.light' : 'grey.700',
                        color: isOwnMessage ? 'white' : 'white'
                      }}
                    >
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {msg.message}
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
              </ListItem>
            );
          })}
        </List>
        <div ref={messagesEndRef} />
      </Box>

      <Divider />

      {/* Input */}
      <Box sx={{ p: 2, bgcolor: 'background.default', position: 'relative' }}>
        {showEmojiPicker && (
          <Box sx={{ position: 'absolute', bottom: 80, right: 20, zIndex: 1000 }}>
            <EmojiPicker onEmojiClick={onEmojiClick} />
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton 
            color="primary"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          >
            <EmojiIcon />
          </IconButton>
          <TextField
            fullWidth
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={!connected}
            variant="outlined"
            size="small"
          />
          <IconButton 
            color="primary"
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || !connected}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
}

export default Chat;

